import React, { useEffect, useState } from "react";
import { Box, styled } from "@mui/material";
import { APIBilling } from "../../../../api";
import { ContainerBg } from "../../../../useGeneralStyles";
import { IntlFormatUSD } from "../../../../utils";
import { CENTS_IN_DOLLAR } from "../../../../constants";
import { ISalesPerCountry } from "../../../../api/billing/index.types";
import {
  ContainerWithLoader,
  PaginationWithLimit,
  SelectByDateWithCustomPeriod,
} from "../../../../components/ui";
import usePagination from "../../../../hooks/usePagination";

const StyledBox = styled(Box)({
  position: "relative",
  borderBottom: "1px solid #e5e5e5",
  padding: "3px 0px 3px 0px",
});

const SalesBreakdownPerCountry = (): React.ReactElement => {
  const pagination = usePagination(5);

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<ISalesPerCountry[]>([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getReport = async (startDate: string, endDate: string, isAllTime?: boolean) => {
    setStartDate(startDate);
    setEndDate(endDate);

    try {
      setIsLoading(true);
      const query = new URLSearchParams();
      query.append("startDate", startDate);
      query.append("endDate", endDate);
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());

      if (isAllTime) {
        query.append("isAllTime", "show");
      }
      const response = await APIBilling.getSalesBreakdownPerCountryRequest(query.toString());

      pagination.setCount(response.data.report[0].rows);
      setData(response.data.report);
    } catch (error) {
      console.error("Error getting SalesBreakdownPerCountry", error);
    } finally {
      setIsLoading(false);
    }
  };
  const max = data?.[0]?.usage || 0;
  const handleChangePage = (page: number) => {
    pagination.setPage(page);
  };
  useEffect(() => {
    (async () => {
      if (startDate && endDate) {
        await getReport(startDate, endDate);
      }
    })();
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    pagination.setPage(0);
  }, [pagination.limit]);

  return (
    <ContainerBg>
      <div>
        <SelectByDateWithCustomPeriod
          getReport={getReport}
          setIsLoading={setIsLoading}
          setReport={setData}
        />
      </div>
      <div>
        <ContainerWithLoader isLoading={isLoading} report={data}>
          <div>
            <Box sx={{ display: "grid", gridTemplateColumns: "50% auto auto" }}>
              <StyledBox>
                <b>Location</b>
              </StyledBox>
              <StyledBox>
                <b>Sales</b>
              </StyledBox>
              <StyledBox>
                <b>$</b>
              </StyledBox>

              {data.map((d) => (
                <React.Fragment key={d.name}>
                  <StyledBox>
                    <div
                      style={{
                        width: `${Math.round((d.usage / max) * 100)}%`,
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        zIndex: -1,
                        backgroundColor: "#cde2f1",
                      }}
                    />
                    {d.name}
                  </StyledBox>
                  <StyledBox>{d.usage}</StyledBox>
                  <Box>{IntlFormatUSD(d.sum_usd / CENTS_IN_DOLLAR)}</Box>
                </React.Fragment>
              ))}
            </Box>

            <PaginationWithLimit
              count={pagination.count}
              limit={pagination.limit}
              setLimit={pagination.setLimit}
              page={pagination.page}
              setPageState={handleChangePage}
            />
          </div>
        </ContainerWithLoader>
      </div>
    </ContainerBg>
  );
};

export default SalesBreakdownPerCountry;
